import { Channel, UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { isNil } from 'lodash';

export function undoCPM(value: number | undefined, unitPriceType: UnitPriceType | undefined) {
  if (unitPriceType === undefined || value === undefined) return 0;
  return unitPriceType.name === 'CPM' ? value / 1000 : value;
}

export function undoCPMById(value: number | undefined, unitPriceTypeId: number | undefined) {
  if (unitPriceTypeId === undefined || value === undefined) return 0;
  return unitPriceTypeId === 1 ? value / 1000 : value;
}

export function adjustForCPM(
  value: number | null | undefined,
  unitPriceType: UnitPriceType | undefined
) {
  if (unitPriceType === undefined || isNil(value)) return 0;
  return unitPriceType.name === 'CPM' ? value * 1000 : value;
}

export function adjustForCPMById(
  value: number | null | undefined,
  unitPriceTypeId: number | undefined
) {
  if (unitPriceTypeId === undefined || isNil(value)) return 0;
  return unitPriceTypeId === 1 ? value * 1000 : value;
}

export function calcEffectivePrice(
  price: number | undefined,
  strategy_referral_comm_rate?: number | undefined | null
): number | undefined {
  if (price == null) return undefined;
  const effectiveRate = strategy_referral_comm_rate ?? 0;
  if (effectiveRate === 0) return price;
  return price * (1 - effectiveRate);
}

export function calcEffectiveUnitPrice(
  unit_price: number | undefined,
  strategy_referral_comm_rate: number | undefined
): number | undefined {
  if (unit_price == null) return undefined;
  const effectiveRate = strategy_referral_comm_rate ?? 0;
  return unit_price * (1 - effectiveRate);
}

export function calcReferralFee(
  price: number | undefined,
  strategy_referral_comm_rate?: number | undefined | null
): number | undefined {
  if (price == null) return undefined;
  const effectiveRate = strategy_referral_comm_rate ?? 0;
  if (effectiveRate === 0) return 0;
  return price * effectiveRate;
}

export function calcGrossPrice(
  price: number | undefined,
  markupAmount: number | undefined
): number | undefined {
  if (price == null) return undefined;
  if (markupAmount == null) return price;
  return price + markupAmount;
}

export function calcAgencyMarkupAmount(
  price: number | undefined,
  strategy_agency_markup_rate?: number | undefined | null
): number | undefined {
  if (price == null) return undefined;
  const effectiveRate = strategy_agency_markup_rate ?? 0;
  if (effectiveRate === 0) return 0;
  if (effectiveRate >= 1) return undefined;
  return price / (1 - effectiveRate) - price;
}

export function calcGrossUnitPrice(
  unit_price: number | undefined,
  strategy_agency_markup_rate: number | undefined | null
): number | undefined {
  if (unit_price == null) return undefined;

  const effectiveRate = strategy_agency_markup_rate ?? 0;
  if (effectiveRate === 0) return 0;
  if (effectiveRate >= 1) return undefined;
  return unit_price / (1 - effectiveRate);
}

export function rateForTacticUnitPriceType(
  channels: Channel[],
  tacticId: number,
  unitPriceTypeId: number
) {
  const rate = channels
    .flatMap(c => c.tactics)
    .find(t => t.id === tacticId)
    ?.rates.find(r => r.unitPriceType.id === unitPriceTypeId);
  if (!rate) {
    throw new Error('Rate not found');
  }
  return rate;
}

export function calcPlannedMargin(
  unit_cost: number | undefined,
  unit_price: number | undefined,
  referral_commission_rate?: number | null
) {
  if (unit_cost === undefined || unit_price === undefined) return undefined;
  const effectiveUnitPrice = unit_price * (1 - (referral_commission_rate ?? 0));
  if (effectiveUnitPrice === 0) return undefined;
  return 1 - unit_cost / effectiveUnitPrice;
}

export function calcMediaBudget(
  price: number | undefined,
  planned_margin?: number | undefined,
  strategy_referral_comm_rate?: number | undefined | null
) {
  if (price == null || planned_margin == null) return undefined;
  const effectiveRate = strategy_referral_comm_rate ?? 0;
  // Database formula: (price * (1 - referral_commission_rate)) * (1 - planned_margin)
  return price * (1 - effectiveRate) * (1 - planned_margin);
}

export function calcEstimatedUnits(numerator: number | undefined, denominator: number | undefined) {
  if (isNil(numerator) || isNil(denominator) || denominator === 0) return undefined;
  return Math.round(numerator / denominator);
}

export function calcZeroDollarMediaBudget(
  estimated_units: number | undefined,
  planned_unit_cost: number | undefined
) {
  if (isNil(estimated_units) || isNil(planned_unit_cost)) return undefined;
  return Math.round(estimated_units * planned_unit_cost * 100) / 100;
}
